import React, { useState } from 'react';
import {
  FormControl,
  InputLabel,
  Chip,
  Box,
  FormHelperText,
  Popover,
  Typography,
  Paper,
  Select,
  MenuItem,
  Checkbox,
  SelectProps,
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import {
  Control,
  Controller,
  FieldError,
  FieldValues,
  RegisterOptions,
} from 'react-hook-form';
import { styled } from '@mui/system';

// Tree node interface
export interface TreeNode {
  id: string;
  label: string;
  selectedLabel: string;
  value: string;
  children?: TreeNode[];
}

interface MultiSelectTreeProps<T extends FieldValues> {
  name: string;
  label: string;
  treeData: TreeNode[];
  control: Control<T>;
  rules?: RegisterOptions<T>;
  error?: FieldError;
  disabled?: boolean;
  readOnly?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  required?: boolean;
  onExpandChange?: (expanded: boolean) => void;
}

const PREFIX = 'MultiSelectTree';

const classes = {
  chip: `${PREFIX}-chip`,
  chipReadOnly: `${PREFIX}-chipReadOnly`,
  treeItemContent: `${PREFIX}-treeItemContent`,
  checkbox: `${PREFIX}-checkbox`,
};

const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  '&.MuiInputLabel-shrink': {
    transform: 'translate(12px, 6px) scale(1)',
  },
}));

const StyledChipBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),

  [`& .${classes.chip}`]: {
    color: theme.palette.lightTextPrimary.main,
    fontWeight: 'normal',
    lineHeight: '18px',
    letterSpacing: 0.16,
    borderColor: theme.palette.lightActionDisabled26p.main,
  },
  [`& .${classes.chipReadOnly}`]: {
    backgroundColor: 'white',
  },
  '& .MuiChip-deleteIcon': {
    width: 13.33,
    height: 13.33,
    color: 'rgba(0, 0, 0, 0.23)',
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  height: 264,
  overflow: 'auto',
  padding: theme.spacing(1),
}));

const StyledTreeView = styled(TreeView)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
}));

const StyledTreeItem = styled(TreeItem)(({ theme }) => ({
  '& .MuiTreeItem-content': {
    height: 32,
    padding: 0,
    borderBottom: '1px solid ' + theme.palette.lightActionDisabled15p.main,
  },
  '& .MuiTreeItem-label': {
    padding: 0,
  },
  '& .MuiTreeItem-iconContainer': {
    paddingLeft: 2,
  },
  [`& .${classes.treeItemContent}`]: {
    display: 'flex',
    alignItems: 'center',
    p: {
      lineHeight: '24px',
    },
  },
  [`& .${classes.checkbox}`]: {
    padding: '4px',
    marginRight: 4,
    '& .MuiSvgIcon-root': {
      fontSize: 24, // overrides size of the checkbox icon
    },
  },
}));

interface StyledSelectProps extends SelectProps {
  readOnly?: boolean;
}

const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'readOnly',
})<StyledSelectProps>(({ theme, readOnly }) => ({
  ...(readOnly && {
    '& .MuiSelect-icon': {
      display: 'none',
    },
    '& .MuiSelect-select': {
      cursor: readOnly ? 'default' : 'pointer',
    },
    pointerEvents: 'none',
    '&:hover': {
      backgroundColor: '#0000000f',
    },
  }),
  ...(!readOnly && {
    '& .MuiSelect-icon': {
      color: `${theme.palette.lightActionDisabled54p.main} !important`,
    },
  }),
}));

const StyledExpandMoreIcon = styled(ExpandMore)(({ theme }) => ({
  fontSize: '24px !important',
  color: theme.palette.lightActionDisabled54p.main,
}));

const StyledChevronRightIcon = styled(ChevronRight)(({ theme }) => ({
  fontSize: '24px !important',
  color: theme.palette.lightActionDisabled54p.main,
}));

/**
 * Select component that presents its select items in a tree view
 * Selected items are displayed as chips.
 */
export function MultiSelectTree<T extends FieldValues>({
  name,
  label,
  treeData,
  control,
  rules = {},
  error,
  size = 'medium',
  fullWidth = true,
  required = false,
  readOnly = false,
  onExpandChange,
}: MultiSelectTreeProps<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);

  const validationRules = {
    ...rules,
    ...(required
      ? {
          validate: {
            notEmpty: (value: string[]) => value && value.length > 0,
            ...rules.validate,
          },
        }
      : {}),
  };

  const handleClick = (event: React.SyntheticEvent<Element, Event>) => {
    setAnchorEl(event.currentTarget as HTMLElement);
    onExpandChange?.(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onExpandChange?.(false);
  };

  const open = Boolean(anchorEl);

  // Flatten tree to get all selectable nodes
  const flattenTree = (nodes: TreeNode[]): TreeNode[] => {
    const result: TreeNode[] = [];

    const traverse = (node: TreeNode) => {
      result.push(node);
      if (node.children) {
        node.children.forEach(traverse);
      }
    };

    nodes.forEach(traverse);
    return result;
  };

  const allNodes = flattenTree(treeData);

  const handleDelete = (
    chipToDelete: string,
    currentValue: string[],
    onChange: (value: string[]) => void,
  ) => {
    const newValue = currentValue.filter((chip) => chip !== chipToDelete);
    onChange(newValue);
  };

  const handleNodeToggle = (
    nodeId: string,
    currentValue: string[],
    onChange: (value: string[]) => void,
  ) => {
    const node = allNodes.find((n) => n.id === nodeId);
    if (!node) return;

    const isSelected = currentValue.includes(node.value);
    let newValue: string[];

    if (isSelected) {
      newValue = currentValue.filter((value) => value !== node.value);
    } else {
      newValue = [...currentValue, node.value];
    }

    onChange(newValue);
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    nodeId: string,
    currentValue: string[],
    onChange: (value: string[]) => void,
  ) => {
    event.stopPropagation();
    handleNodeToggle(nodeId, currentValue, onChange);
  };

  const renderTree = (
    nodes: TreeNode[],
    currentValue: string[],
    onChange: (value: string[]) => void,
  ) => {
    return nodes.map((node) => {
      const isSelected = currentValue.includes(node.value);

      return (
        <StyledTreeItem
          key={node.id}
          nodeId={node.id}
          label={
            <Box
              className={classes.treeItemContent}
              onClick={(e) => {
                e.stopPropagation();
                handleNodeToggle(node.id, currentValue, onChange);
              }}>
              <Checkbox
                checked={isSelected}
                className={classes.checkbox}
                onChange={(e) =>
                  handleCheckboxChange(e, node.id, currentValue, onChange)
                }
                size="small"
                onClick={(e) => e.stopPropagation()}
              />
              <Typography variant="body2">{node.label}</Typography>
            </Box>
          }>
          {node.children && renderTree(node.children, currentValue, onChange)}
        </StyledTreeItem>
      );
    });
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      error={!!error}
      size={size}
      required={required}>
      <StyledInputLabel>{label}</StyledInputLabel>
      <Controller
        name={name as any}
        control={control}
        rules={validationRules}
        defaultValue={[] as any}
        render={({ field: { onChange, value, onBlur, ref } }) => (
          <>
            <StyledSelect
              ref={ref}
              labelId={`${name}-label`}
              id={name}
              multiple
              value={value || []}
              onChange={() => {}} // Prevent default change behavior
              onBlur={onBlur}
              onOpen={handleClick}
              open={false} // Keep closed, we use popover instead
              inputProps={{ readOnly }}
              disableUnderline={readOnly}
              variant={readOnly ? 'filled' : 'outlined'}
              readOnly={readOnly}
              displayEmpty
              renderValue={(selected) => {
                return (
                  <StyledChipBox>
                    {(selected as string[]).map((selectedValue) => {
                      const node = allNodes.find(
                        (n) => n.value === selectedValue,
                      );
                      return (
                        <Chip
                          className={`${classes.chip} ${readOnly ? classes.chipReadOnly : undefined}`}
                          key={selectedValue}
                          label={node?.selectedLabel || selectedValue}
                          onDelete={
                            readOnly
                              ? undefined
                              : () =>
                                  handleDelete(selectedValue, value, onChange)
                          }
                          onMouseDown={(event) => {
                            event.stopPropagation();
                          }}
                          size="small"
                          variant="outlined"
                        />
                      );
                    })}
                  </StyledChipBox>
                );
              }}>
              <MenuItem value="" sx={{ display: 'none' }} />
            </StyledSelect>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  sx: {
                    width: anchorEl?.offsetWidth || 300,
                  },
                },
              }}>
              <StyledPaper>
                <StyledTreeView
                  defaultCollapseIcon={<StyledExpandMoreIcon />}
                  defaultExpandIcon={<StyledChevronRightIcon />}
                  expanded={expanded}
                  onNodeToggle={(event, nodeIds) => setExpanded(nodeIds)}>
                  {renderTree(treeData, value || [], onChange)}
                </StyledTreeView>
              </StyledPaper>
            </Popover>
          </>
        )}
      />
      {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  );
}
